import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Button, Labelbox, InfoTable } from "..";
import PhoneInput from "../PhoneInput";
import { CLIENT_ACTION, MASTER_ACTION } from "../../redux/action";
import { getClientById } from "../../redux/action/clientAction";
import { BUSINESS_TYPE_OPTIONS } from "../../constants/businessTypes";
import {
  getClientBusinesses,
  mergeBusinessesIntoClient,
  promoteClientToBusinessAccount,
} from "../../utils/clientBusinessAccess";
import { Toaster } from "../../utils/common";
import { normalizeToE164 } from "../../utils/phoneUtils";
import "./client-business-manager.scss";

const emptyBusinessForm = () => ({
  business_name: "",
  business_type: "",
  business_phone: "",
  business_address_1: "",
  business_country_id: "",
  business_state_id: "",
  business_city_id: "",
  business_zipcode: "",
});

const BUSINESS_INFO_KEYS = [
  "business_name",
  "business_type",
  "business_phone",
  "business_address_1",
  "business_city",
  "business_state",
  "business_zipcode",
  "business_country",
];

const BUSINESS_INFO_TITLES = [
  "Business Name:",
  "Type:",
  "Phone:",
  "Address 1:",
  "City:",
  "State Incorporated:",
  "Zip Code:",
  "Country:",
];

function resolveCountryName(countryOptions, countryId) {
  if (!countryId) return "-";
  const match = (countryOptions || []).find((c) => {
    const cId = c.country_id || c.id;
    return (
      Number(cId) === Number(countryId) || String(cId) === String(countryId)
    );
  });
  return match?.value || match?.country_name || match?.name || match?.country || "-";
}

function resolveStateName(stateOptions, stateId) {
  if (!stateId) return "-";
  const match = (stateOptions || []).find((s) => {
    const sId = s.state_id || s.id;
    return Number(sId) === Number(stateId) || String(sId) === String(stateId);
  });
  return match?.value || match?.state_name || match?.name || match?.state || "-";
}

function businessToInfoData(business, countryOptions, stateOptionsByCountry) {
  const stateOptions = stateOptionsByCountry[business.business_country_id] || [];
  return {
    business_name: business.business_name || "-",
    business_type: business.business_type || "-",
    business_phone: business.business_phone || "-",
    business_address_1: business.business_address_1 || "-",
    business_city: business.business_city_id || "-",
    business_state: resolveStateName(stateOptions, business.business_state_id),
    business_zipcode: business.business_zipcode || "-",
    business_country: resolveCountryName(
      countryOptions,
      business.business_country_id,
    ),
  };
}
function businessSummaryAddress(business) {
  return [
    business.business_address_1,
    business.business_city_id,
    business.business_zipcode,
  ]
    .filter(Boolean)
    .join(", ");
}

function businessToForm(business = {}) {
  return {
    business_name: business.business_name || "",
    business_type: business.business_type || "",
    business_phone: business.business_phone || "",
    business_address_1: business.business_address_1 || "",
    business_country_id: business.business_country_id || "",
    business_state_id: business.business_state_id || "",
    business_city_id: business.business_city_id || "",
    business_zipcode: business.business_zipcode || "",
  };
}

const ClientBusinessManager = ({
  clientId,
  clientRecord = null,
  canEdit = false,
  allowDelete = true,
  onClientUpdated,
}) => {
  const dispatch = useDispatch();
  const { business_country: countryOptions = [], business_state: formStateOptions = [] } =
    useSelector((state) => state.master || {});
  const [stateOptionsByCountry, setStateOptionsByCountry] = useState({});
  const loadedStateCountriesRef = useRef(new Set());

  const onClientUpdatedRef = useRef(onClientUpdated);
  onClientUpdatedRef.current = onClientUpdated;

  const initialBusinesses = getClientBusinesses(clientRecord);
  const [businesses, setBusinesses] = useState(initialBusinesses);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [formData, setFormData] = useState(emptyBusinessForm);
  const [submitting, setSubmitting] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuBusiness, setMenuBusiness] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expandedBusinessIds, setExpandedBusinessIds] = useState(() => new Set());

  const getBusinessKey = (business) =>
    String(business.client_business_id || business.business_name);

  const toggleExpanded = (business) => {
    const key = getBusinessKey(business);
    setExpandedBusinessIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const hasLoadedRef = useRef(false);
  const clientIdRef = useRef(clientId);

  const notifyClientUpdated = useCallback(async (detail, list) => {
    const callback = onClientUpdatedRef.current;
    if (!callback || !detail) return;
    const promoted = promoteClientToBusinessAccount(
      mergeBusinessesIntoClient(detail, list),
      list,
    );
    if (String(detail.client_type || "").trim() === "Individual") {
      promoted.client_type = "Individual";
    }
    callback(promoted);
  }, []);

  const clientRecordRef = useRef(clientRecord);
  clientRecordRef.current = clientRecord;

  const refreshBusinesses = useCallback(async () => {
    if (!clientId) return [];

    setLoading(true);
    try {
      const apiList = await CLIENT_ACTION.getClientBusinesses(clientId);
      if (Array.isArray(apiList)) {
        setBusinesses(apiList);
        return apiList;
      }
    } catch {
      /* use legacy client record below */
    } finally {
      setLoading(false);
    }

    const legacyList = getClientBusinesses(clientRecordRef.current);
    setBusinesses(legacyList);
    return legacyList;
  }, [clientId]);

  useEffect(() => {
    if (!clientId) {
      setBusinesses(getClientBusinesses(clientRecordRef.current));
      return;
    }

    if (clientIdRef.current !== clientId) {
      clientIdRef.current = clientId;
      hasLoadedRef.current = false;
    }

    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    refreshBusinesses();
  }, [clientId, refreshBusinesses]);

  useEffect(() => {
    if (!businesses.length) return;
    dispatch(MASTER_ACTION.getMasterData({ name: "country" }));
  }, [businesses.length, dispatch]);

  useEffect(() => {
    if (!businesses.length) return;

    const countryIds = [
      ...new Set(
        businesses.map((b) => b.business_country_id).filter(Boolean),
      ),
    ];

    let cancelled = false;
    countryIds.forEach(async (countryId) => {
      if (loadedStateCountriesRef.current.has(countryId)) return;
      loadedStateCountriesRef.current.add(countryId);
      try {
        const states = await dispatch(
          MASTER_ACTION.getMasterData({
            name: "state",
            name_type: "business_state",
            id: countryId,
          }),
        ).unwrap();
        if (!cancelled) {
          setStateOptionsByCountry((prev) => ({
            ...prev,
            [countryId]: states || [],
          }));
        }
      } catch {
        loadedStateCountriesRef.current.delete(countryId);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [businesses, dispatch]);

  useEffect(() => {
    if (!modalOpen) return;
    dispatch(MASTER_ACTION.getMasterData({ name: "country" }));
    if (formData.business_country_id) {
      dispatch(
        MASTER_ACTION.getMasterData({
          name: "state",
          name_type: "business_state",
          id: formData.business_country_id,
        }),
      );
    }
  }, [modalOpen, dispatch, formData.business_country_id]);

  const openAddModal = () => {
    setEditingBusiness(null);
    setFormData(emptyBusinessForm());
    setModalOpen(true);
  };

  const openEditModal = (business) => {
    setEditingBusiness(business);
    setFormData(businessToForm(business));
    setModalOpen(true);
    setMenuAnchor(null);
    setMenuBusiness(null);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingBusiness(null);
    setFormData(emptyBusinessForm());
  };

  const updateField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCountryChange = (value) => {
    updateField("business_country_id", value);
    updateField("business_state_id", "");
    if (value) {
      dispatch(
        MASTER_ACTION.getMasterData({
          name: "state",
          name_type: "business_state",
          id: value,
        }),
      );
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!clientId || submitting) return;

    if (!formData.business_name.trim() || !formData.business_type) {
      Toaster.warning("Business name and type are required.");
      return;
    }

    setSubmitting(true);
    try {
      const normalizeNullableInt = (val) => {
        if (val === "" || val === undefined || val === null) return null;
        return val;
      };

      const payload = {
        client_id: clientId,
        business_name: formData.business_name.trim(),
        business_type: formData.business_type,
        business_phone: formData.business_phone
          ? normalizeToE164(formData.business_phone) ||
            formData.business_phone.trim()
          : "",
        business_address_1: formData.business_address_1.trim(),
        business_country_id: normalizeNullableInt(formData.business_country_id),
        business_state_id: normalizeNullableInt(formData.business_state_id),
        business_city_id: formData.business_city_id.trim(),
        business_zipcode: formData.business_zipcode.trim(),
      };

      let result;
      if (editingBusiness?.client_business_id) {
        result = await CLIENT_ACTION.updateClientBusiness(
          editingBusiness.client_business_id,
          payload,
        );
      } else {
        result = await CLIENT_ACTION.createClientBusiness({
          ...payload,
          is_primary: businesses.length === 0,
        });
      }

      if (result?.status === 1) {
        Toaster.success(
          editingBusiness ? "Business updated." : "Business added.",
        );
        closeModal();
        const list = await refreshBusinesses();
        const detail = await dispatch(getClientById(clientId)).unwrap();
        if (detail) await notifyClientUpdated(detail, list);
      } else {
        Toaster.error(result?.message || "Unable to save business.");
      }
    } catch (err) {
      console.error(err);
      Toaster.error("An error occurred while saving.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetPrimary = async (business) => {
    if (!business?.client_business_id || !clientId) return;
    setMenuAnchor(null);
    setMenuBusiness(null);
    try {
      const result = await CLIENT_ACTION.setPrimaryClientBusiness(
        business.client_business_id,
        clientId,
      );
      if (result?.status === 1) {
        Toaster.success("Primary business updated.");
        const list = await refreshBusinesses();
        const detail = await dispatch(getClientById(clientId)).unwrap();
        if (detail) await notifyClientUpdated(detail, list);
      } else {
        Toaster.error(result?.message || "Unable to set primary business.");
      }
    } catch {
      Toaster.error("An error occurred.");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.client_business_id || !clientId) return;
    setSubmitting(true);
    try {
      const result = await CLIENT_ACTION.deleteClientBusiness(
        deleteTarget.client_business_id,
        clientId,
      );
      if (result?.status === 1) {
        Toaster.success("Business removed.");
        setDeleteTarget(null);
        const list = await refreshBusinesses();
        const detail = await dispatch(getClientById(clientId)).unwrap();
        if (detail) await notifyClientUpdated(detail, list);
      } else {
        Toaster.error(result?.message || "Unable to delete business.");
      }
    } catch {
      Toaster.error("An error occurred.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="client-business-manager">
      {canEdit && (
        <div className="client-business-manager-header">
          <Button
            text="Add business"
            variant="outline"
            handleClick={openAddModal}
          />
        </div>
      )}

      {loading && !businesses.length ? (
        <p className="client-business-manager-empty">Loading businesses…</p>
      ) : !businesses.length ? (
        <div className="client-business-manager-empty">
          <p>No businesses registered yet.</p>
          {canEdit && (
            <Button text="Add your business" handleClick={openAddModal} />
          )}
        </div>
      ) : (
        <ul className="client-business-list">
          {businesses.map((business) => {
            const businessKey = getBusinessKey(business);
            const isExpanded = expandedBusinessIds.has(businessKey);
            const summaryAddress = businessSummaryAddress(business);

            return (
            <li
              key={businessKey}
              className={`client-business-card${isExpanded ? " is-expanded" : ""}`}
            >
              <div className="client-business-card-row">
                <div className="client-business-card-main">
                  <div className="client-business-card-title-row">
                    <strong>{business.business_name}</strong>
                    {business.is_primary && (
                      <span className="client-business-primary-badge">Primary</span>
                    )}
                  </div>
                  <div className="client-business-card-meta">
                    <span>{business.business_type || "—"}</span>
                    {business.business_phone && (
                      <span>{business.business_phone}</span>
                    )}
                  </div>
                  {summaryAddress && (
                    <p className="client-business-card-address">{summaryAddress}</p>
                  )}
                </div>
                <div className="client-business-card-actions">
                  <IconButton
                    size="small"
                    className="client-business-expand-btn"
                    aria-label={isExpanded ? "Collapse details" : "Expand details"}
                    aria-expanded={isExpanded}
                    onClick={() => toggleExpanded(business)}
                  >
                    {isExpanded ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                  {canEdit && (
                    <>
                      <IconButton
                        size="small"
                        className="client-business-card-menu"
                        aria-label="Business actions"
                        onClick={(e) => {
                          setMenuAnchor(e.currentTarget);
                          setMenuBusiness(business);
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                      <Menu
                        anchorEl={menuAnchor}
                        open={
                          Boolean(menuAnchor) &&
                          menuBusiness?.client_business_id ===
                            business.client_business_id
                        }
                        onClose={() => {
                          setMenuAnchor(null);
                          setMenuBusiness(null);
                        }}
                      >
                        <MenuItem onClick={() => openEditModal(business)}>
                          <ListItemIcon>
                            <EditIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Edit</ListItemText>
                        </MenuItem>
                        {!business.is_primary && business.client_business_id && (
                          <MenuItem onClick={() => handleSetPrimary(business)}>
                            <ListItemIcon>
                              <StarBorderIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Set as primary</ListItemText>
                          </MenuItem>
                        )}
                        {business.is_primary && (
                          <MenuItem disabled>
                            <ListItemIcon>
                              <StarIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText>Primary business</ListItemText>
                          </MenuItem>
                        )}
                        {allowDelete && business.client_business_id && (
                          <MenuItem
                            onClick={() => {
                              setDeleteTarget(business);
                              setMenuAnchor(null);
                              setMenuBusiness(null);
                            }}
                          >
                            <ListItemIcon>
                              <DeleteIcon fontSize="small" color="error" />
                            </ListItemIcon>
                            <ListItemText>Delete</ListItemText>
                          </MenuItem>
                        )}
                      </Menu>
                    </>
                  )}
                </div>
              </div>
              {isExpanded && (
                <div className="client-business-card-details">
                  <InfoTable
                    transparent
                    titles={BUSINESS_INFO_TITLES}
                    keys={BUSINESS_INFO_KEYS}
                    info={businessToInfoData(
                      business,
                      countryOptions,
                      stateOptionsByCountry,
                    )}
                  />
                </div>
              )}
            </li>
            );
          })}
        </ul>
      )}

      <Dialog open={modalOpen} onClose={closeModal} maxWidth="sm" fullWidth>
        <form
          className="client-business-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(e);
          }}
        >
          <h3>{editingBusiness ? "Edit business" : "Add business"}</h3>
          <div className="client-business-form-grid">
            <Labelbox
              type="text"
              labelname="Business name"
              value={formData.business_name}
              changeData={(val) => updateField("business_name", val)}
              required
            />
            <Labelbox
              type="select"
              labelname="Type"
              value={formData.business_type}
              changeData={(val) => updateField("business_type", val)}
              dropdown={BUSINESS_TYPE_OPTIONS}
              required
            />
            <PhoneInput
              labelname="Business phone"
              value={formData.business_phone || ""}
              onChange={(val) => updateField("business_phone", val)}
            />
            <Labelbox
              type="text"
              labelname="Address"
              value={formData.business_address_1}
              changeData={(val) => updateField("business_address_1", val)}
            />
            <Labelbox
              type="select"
              labelname="Country"
              value={formData.business_country_id}
              changeData={handleCountryChange}
              dropdown={countryOptions}
            />
            <Labelbox
              type="select"
              labelname="State"
              value={formData.business_state_id}
              changeData={(val) => updateField("business_state_id", val)}
              dropdown={formStateOptions}
              disabled={!formData.business_country_id}
            />
            <Labelbox
              type="text"
              labelname="City"
              value={formData.business_city_id}
              changeData={(val) => updateField("business_city_id", val)}
            />
            <Labelbox
              type="text"
              labelname="Zip code"
              value={formData.business_zipcode}
              changeData={(val) => updateField("business_zipcode", val)}
            />
          </div>
          <div className="client-business-form-actions">
            <Button
              text="Cancel"
              variant="outline"
              handleClick={closeModal}
              disable={submitting}
            />
            <Button
              text={submitting ? "Saving…" : "Save"}
              handleClick={handleSubmit}
              disable={submitting}
            />
          </div>
        </form>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <div className="client-business-delete-dialog">
          <h3>Delete business?</h3>
          <p>
            Remove <strong>{deleteTarget?.business_name}</strong>? This cannot be
            undone.
          </p>
          <div className="client-business-form-actions">
            <Button
              text="Cancel"
              variant="outline"
              handleClick={() => setDeleteTarget(null)}
              disable={submitting}
            />
            <Button
              text={submitting ? "Deleting…" : "Delete"}
              handleClick={handleDelete}
              disable={submitting}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ClientBusinessManager;
